// The in-memory shape of a loaded capture. Everything downstream of the parser
// works from these typed arrays; nothing re-reads text.

/** Sentinel for a bin with no reading. Stored in centi-dB, so -327.68 dB is unreachable. */
export const MISSING = -32768

/**
 * The slice layout rtl_power used. A sweep is N evenly spaced slices of K bins
 * that do not necessarily tile, so a bin's frequency comes from its slice's own
 * start plus its offset within the slice.
 */
export interface Geometry {
  sliceLowsHz: number[]
  binsPerSlice: number
  stepHz: number
}

export interface Run {
  /** Zero-based position in the file. */
  id: number
  geometry: Geometry
  binCount: number
  /** Center frequency of every bin, ascending. */
  binHz: Float64Array
  sweepCount: number
  /** Epoch ms of each sweep, ascending. */
  tsMs: Float64Array
  /** Median interval between sweeps; the height of one row on the time axis. */
  cadenceMs: number
  /** Number of cadence slots from the first sweep to the last, inclusive. */
  slotCount: number
  /** Sweep index for each slot, or -1 where no sweep landed. */
  slotSweep: Int32Array
  /** sweepCount × binCount, row-major, centi-dB, MISSING for absent bins. */
  dbm: Int16Array
  /** 1 where the sweep had every bin, else 0. */
  complete: Uint8Array
  /** Extremes over readings that exist, in dB. */
  minDb: number
  maxDb: number
  /** Per-bin median over the run, in dB, NaN where a bin never had a reading. */
  floorDb: Float32Array
  /** rtl_power's own sample count, from the first line of the first sweep. */
  samples: number
  /** Human-readable notes gathered while assembling: torn sweeps, stray lines. */
  diagnostics: string[]
}

/** Buffers that can be transferred across the worker boundary without copying. */
export function runBuffers(run: Run): ArrayBuffer[] {
  return [
    run.binHz.buffer as ArrayBuffer,
    run.tsMs.buffer as ArrayBuffer,
    run.slotSweep.buffer as ArrayBuffer,
    run.dbm.buffer as ArrayBuffer,
    run.complete.buffer as ArrayBuffer,
    run.floorDb.buffer as ArrayBuffer,
  ]
}

export function centiToDb(centi: number): number {
  return centi / 100
}

export function dbToCenti(db: number): number {
  const centi = Math.round(db * 100)
  if (centi <= MISSING) return MISSING + 1
  if (centi > 32767) return 32767
  return centi
}
